import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ICONS, Icon } from 'components/Icon';
import {
  ModalScreens,
  NavigationStacks,
  Screens,
  Stacks,
} from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import { Txt } from 'components/Txt';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { useScreenSize } from 'hooks/useScreenSize';
import { pluraliseXOfX } from 'lib/pluralise';
import { smartText } from 'lib/str';
import { theme } from 'lib/theme';
import { orderBy } from 'lodash';
import React from 'react';
import { View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import {
  NAVIGATE_MODAL,
  doSetupEngageAddElements,
  doSetupEngageOrderElements,
  doSetupEngageAddDocuments,
  doSetupEngageOrderDocuments,
  doSetupEngageRemoveDocument,
  doSetupEngageRemoveElement,
  doSetupEngageUpdateGeneral,
  doSetupEngageClose,
  doSetupEngageElementUpdate,
  doSetupEngageDocumentUpdate,
} from 'store/actions';
import {
  selectEngageSettings,
  selectRemoteOrganisationId,
} from 'store/remote/selectors';
import { ActionType } from 'typesafe-actions';
import { EngageSettingsActions } from './EngageSettingsActions';

type Props = NativeStackScreenProps<
  NavigationStacks[Stacks.SETTINGS],
  Screens.SettingsEngage
>;

export const EngageSettingsScreen: React.FC<Props> = ({ navigation }) => {
  const dispatch = useDispatch();
  const screenSize = useScreenSize();

  const organisationId = useSelector(selectRemoteOrganisationId);
  const engage = useSelector(selectEngageSettings);

  // Elements
  const enabledElements = React.useMemo(() => {
    return orderBy(
      (engage?.elements || []).filter((element) => element.isSet),
      'ordering',
    );
  }, [engage?.elements]);
  const hasDisabledElements =
    enabledElements.length !== (engage?.elements.length || 0);

  // Documents
  const enabledDocuments = React.useMemo(() => {
    return orderBy(
      (engage?.documents || []).filter((element) => element.isSet),
      'ordering',
    );
  }, [engage?.documents]);
  const hasDisabledDocuments =
    enabledDocuments.length !== (engage?.documents.length || 0);

  React.useEffect(() => {
    return () => {
      dispatch(doSetupEngageClose());
    };
  }, [dispatch]);

  React.useEffect(() => {
    if (!organisationId) {
      navigation.goBack();
    }
  }, [navigation, organisationId]);

  if (!organisationId || !engage) {
    return null;
  }

  const handleChangeValue = (
    data: ActionType<typeof doSetupEngageUpdateGeneral>['payload']['data'],
  ) => dispatch(doSetupEngageUpdateGeneral({ data }));

  return (
    <PageScrollView
      isInModal={false}
      header={
        <NavigationHeader
          heading="Engage"
          onPressBackButton={
            screenSize === 'large' ? undefined : navigation.goBack
          }
        />
      }
    >
      <Card explanation="Engage allows you to provide public access to some of your data held in RiskBase." />

      <SeparatorHorizontal withMarginBottom />

      <Card explanation="You can toggle Engage on and off without affecting your other settings">
        <CardRow
          label="Enable Engage"
          checkedValue={engage.enable}
          onPress={() => handleChangeValue({ enable: !engage.enable })}
        />
      </Card>

      <SeparatorHorizontal withMarginBottom />

      <Card label="Asset" marginBottomQuarter>
        <CardRow
          label="Enable Asset Page"
          checkedValue={engage.enableAsset}
          onPress={() =>
            handleChangeValue({ enableAsset: !engage.enableAsset })
          }
        />
      </Card>

      <Card marginBottomQuarter>
        <CardRow
          label="Show Responsible People"
          checkedValue={engage.showResponsiblePeople}
          onPress={() =>
            handleChangeValue({
              showResponsiblePeople: !engage.showResponsiblePeople,
            })
          }
        />
        <CardRow
          label="Show Competent People"
          checkedValue={engage.showCompetentPeople}
          onPress={() =>
            handleChangeValue({
              showCompetentPeople: !engage.showCompetentPeople,
            })
          }
        />
        <CardRow
          label="Show Accountable People"
          checkedValue={engage.showAccountablePeople}
          onPress={() =>
            handleChangeValue({
              showAccountablePeople: !engage.showAccountablePeople,
            })
          }
        />
      </Card>

      <Card>
        <CardRow
          label="Show Evacuation Strategy"
          checkedValue={engage.showEvacuationStrategy}
          onPress={() =>
            handleChangeValue({
              showEvacuationStrategy: !engage.showEvacuationStrategy,
            })
          }
        />
      </Card>

      <SeparatorHorizontal withMarginBottom />

      <EngageSettingsActions
        label="Asset Actions"
        explanation={smartText(
          'See "Element Types" below to setup action settings for different element types',
        )}
        enableTasks={engage.enableTasks}
        enableTaskCategory={engage.enableTaskCategory}
        enableTaskDueDate={engage.enableTaskDueDate}
        enableTaskPhotos={engage.enableTaskPhotos}
        enableTaskSeverity={engage.enableTaskSeverity}
        enableTaskTimescale={engage.enableTaskTimescale}
        onChange={(key, value) => handleChangeValue({ [key]: value })}
      />

      <SeparatorHorizontal withMarginBottom />

      <Card
        label="Messaging"
        labelLinkText="Change Settings"
        onLabelLinkPress={() =>
          dispatch(
            NAVIGATE_MODAL({
              screen: ModalScreens.SettingsEngageMessaging,
              params: {
                messaging: {
                  replyToEmail: engage.replyToEmail,
                },
              },
            }),
          )
        }
        explanation={
          'When Engage sends an email, replies from recipients will go to this address'
        }
      >
        <CardRow
          label="Reply-To Email"
          value={engage.replyToEmail}
          placeholder="support@riskbase.uk"
        />
      </Card>

      <SeparatorHorizontal withMarginBottom />

      <Card
        label="Report an Issue"
        labelLinkText="Change Settings"
        onLabelLinkPress={() =>
          dispatch(
            NAVIGATE_MODAL({
              screen: ModalScreens.SettingsEngageReportIssue,
              params: {
                reportIssue: {
                  enableReportIssue: engage.enableReportIssue,
                  reportIssueTitle: engage.reportIssueTitle,
                  reportIssueDescription: engage.reportIssueDescription,
                  reportIssueWebsite: engage.reportIssueWebsite,
                  reportIssueEmail: engage.reportIssueEmail,
                  reportIssuePhone: engage.reportIssuePhone,
                },
              },
            }),
          )
        }
        marginBottomQuarter
      >
        <CardRow
          checkedValue={engage.enableReportIssue}
          label="Show Report an Issue"
        />
      </Card>
      <Card>
        <CardRow
          label={engage.reportIssueTitle || 'Report an Issue'}
          label2={engage.reportIssueDescription}
          labelAlignTop
        >
          <View style={{ rowGap: 6, marginTop: 6 }}>
            <View
              style={{
                flexDirection: 'row',
                columnGap: 6,
                alignItems: 'center',
              }}
            >
              <Icon source={ICONS.GlobeSolid} />
              {engage.reportIssueWebsite ? (
                <Txt size="xsmall">{engage.reportIssueWebsite}</Txt>
              ) : (
                <Txt size="xsmall" style={{ color: theme.colors.placeholder }}>
                  No Website
                </Txt>
              )}
            </View>
            <View
              style={{
                flexDirection: 'row',
                columnGap: 6,
                alignItems: 'center',
              }}
            >
              <Icon source={ICONS.CircleEnvelopeSolid} />
              {engage.reportIssueEmail ? (
                <Txt size="xsmall">{engage.reportIssueEmail}</Txt>
              ) : (
                <Txt size="xsmall" style={{ color: theme.colors.placeholder }}>
                  No Email
                </Txt>
              )}
            </View>
            <View
              style={{
                flexDirection: 'row',
                columnGap: 6,
                alignItems: 'center',
              }}
            >
              <Icon source={ICONS.CirclePhoneSolid} />
              {engage.reportIssuePhone ? (
                <Txt size="xsmall">{engage.reportIssuePhone}</Txt>
              ) : (
                <Txt size="xsmall" style={{ color: theme.colors.placeholder }}>
                  No Phone
                </Txt>
              )}
            </View>
          </View>
        </CardRow>
      </Card>

      <SeparatorHorizontal withMarginBottom />

      {(!!enabledDocuments.length || hasDisabledDocuments) && (
        <>
          <Card
            label="Document Types"
            labelLinkText="Change Order"
            onLabelLinkPress={
              enabledElements.length
                ? () => dispatch(doSetupEngageOrderDocuments())
                : null
            }
            margin={!hasDisabledDocuments}
            marginBottomQuarter={
              hasDisabledDocuments && !!enabledDocuments.length
            }
          >
            {enabledDocuments.map((document) => (
              <CardRow
                key={document.id}
                label={document.name}
                label2={document.description}
                label2NumberOfLines={2}
                labelAlignTop
                indentActions
                showMenuOnPress
                menuOptions={[
                  {
                    text: 'Change Settings',
                    onPress: () =>
                      dispatch(
                        NAVIGATE_MODAL({
                          screen: ModalScreens.SettingsEngageDocument,
                          params: {
                            document: { ...document },
                          },
                        }),
                      ),
                  },
                  '-',
                  {
                    text: 'On Asset Page',
                    textSize: 'xsmall',
                    onPress: () =>
                      dispatch(
                        doSetupEngageDocumentUpdate({
                          document: {
                            ...document,
                            enableAsset: !document.enableAsset,
                          },
                        }),
                      ),
                    leftIcon: document.enableAsset
                      ? ICONS.CheckLight
                      : ICONS.XmarkLight,
                    leftIconColor: document.enableAsset
                      ? theme.colors.green
                      : theme.colors.red,
                  },
                  '-',
                  {
                    text: 'Remove Document Type',
                    onPress: () =>
                      dispatch(
                        doSetupEngageRemoveDocument({
                          documentId: document.id,
                        }),
                      ),
                    destructive: true,
                  },
                ]}
              >
                <ViewWithSeparators
                  horizontal
                  style={{ flexWrap: 'wrap' }}
                  separator={<Txt size="xsmall"> • </Txt>}
                >
                  <Txt
                    size="xsmall"
                    style={
                      document.enableAsset
                        ? { color: theme.colors.green }
                        : { color: theme.colors.red }
                    }
                  >
                    Asset
                  </Txt>
                </ViewWithSeparators>
              </CardRow>
            ))}
          </Card>
          {hasDisabledDocuments && (
            <Card>
              <CardRow
                label="Add document types..."
                labelColor={theme.colors.blue}
                icon={ICONS.PlusLight}
                iconColor={theme.colors.blue}
                onPress={() => dispatch(doSetupEngageAddDocuments())}
              />
            </Card>
          )}
        </>
      )}

      <SeparatorHorizontal withMarginBottom />

      {(!!enabledElements.length || hasDisabledElements) && (
        <>
          <Card
            label="Element Types"
            labelLinkText="Change Order"
            onLabelLinkPress={
              enabledElements.length
                ? () => dispatch(doSetupEngageOrderElements())
                : null
            }
            margin={!hasDisabledElements}
            marginBottomQuarter={
              hasDisabledElements && !!enabledElements.length
            }
          >
            {enabledElements.map((element) => (
              <CardRow
                key={element.id}
                label={`${element.name} (${element.sectionName})`}
                label2={element.description}
                label2NumberOfLines={2}
                labelAlignTop
                indentActions
                showMenuOnPress
                menuOptions={[
                  {
                    text: 'Change Settings',
                    onPress: () =>
                      dispatch(
                        NAVIGATE_MODAL({
                          screen: ModalScreens.SettingsEngageElement,
                          params: {
                            element: { ...element },
                          },
                        }),
                      ),
                  },
                  '-',
                  {
                    text: 'Details Page',
                    textSize: 'xsmall',
                    onPress: () =>
                      dispatch(
                        doSetupEngageElementUpdate({
                          element: {
                            ...element,
                            enablePage: !element.enablePage,
                          },
                        }),
                      ),
                    leftIcon: element.enablePage
                      ? ICONS.CheckLight
                      : ICONS.XmarkLight,
                    leftIconColor: element.enablePage
                      ? theme.colors.green
                      : theme.colors.red,
                  },
                  {
                    text: 'Listing Page',
                    textSize: 'xsmall',
                    onPress: () =>
                      dispatch(
                        doSetupEngageElementUpdate({
                          element: {
                            ...element,
                            enableListing: !element.enableListing,
                          },
                        }),
                      ),
                    leftIcon: element.enableListing
                      ? ICONS.CheckLight
                      : ICONS.XmarkLight,
                    leftIconColor: element.enableListing
                      ? theme.colors.green
                      : theme.colors.red,
                  },
                  {
                    text: 'On Asset Page',
                    textSize: 'xsmall',
                    onPress: () =>
                      dispatch(
                        doSetupEngageElementUpdate({
                          element: {
                            ...element,
                            enableAsset: !element.enableAsset,
                          },
                        }),
                      ),
                    leftIcon: element.enableAsset
                      ? ICONS.CheckLight
                      : ICONS.XmarkLight,
                    leftIconColor: element.enableAsset
                      ? theme.colors.green
                      : theme.colors.red,
                  },
                  '-',
                  {
                    text: 'Remove Element Type',
                    onPress: () =>
                      dispatch(
                        doSetupEngageRemoveElement({ elementId: element.id }),
                      ),
                    destructive: true,
                  },
                ]}
              >
                <ViewWithSeparators
                  horizontal
                  style={{ flexWrap: 'wrap' }}
                  separator={<Txt size="xsmall"> • </Txt>}
                >
                  <Txt
                    size="xsmall"
                    style={
                      element.enablePage
                        ? { color: theme.colors.green }
                        : { color: theme.colors.red }
                    }
                  >
                    Details
                  </Txt>
                  <Txt
                    size="xsmall"
                    style={
                      element.enableListing
                        ? { color: theme.colors.green }
                        : { color: theme.colors.red }
                    }
                  >
                    Listing
                  </Txt>
                  <Txt
                    size="xsmall"
                    style={
                      element.enableAsset
                        ? { color: theme.colors.green }
                        : { color: theme.colors.red }
                    }
                  >
                    Asset
                  </Txt>
                  <Txt
                    size="xsmall"
                    style={
                      element.enableTasks
                        ? { color: theme.colors.green }
                        : { color: theme.colors.red }
                    }
                  >
                    Actions
                  </Txt>
                  {!!element.fields.length && (
                    <Txt size="xsmall">
                      {pluraliseXOfX(
                        element.fields.filter((f) => f.enable).length,
                        element.fields.length,
                        '1 Field',
                        '{x} Fields',
                        '',
                        '/',
                      )}
                    </Txt>
                  )}
                  {!!element.checks.length && (
                    <Txt size="xsmall">
                      {pluraliseXOfX(
                        element.checks.filter((c) => c.enable).length,
                        element.checks.length,
                        '1 Check',
                        '{x} Checks',
                        '',
                        '/',
                      )}
                    </Txt>
                  )}
                </ViewWithSeparators>
              </CardRow>
            ))}
          </Card>
          {hasDisabledElements && (
            <Card>
              <CardRow
                label="Add element types..."
                labelColor={theme.colors.blue}
                icon={ICONS.PlusLight}
                iconColor={theme.colors.blue}
                onPress={() => dispatch(doSetupEngageAddElements())}
              />
            </Card>
          )}
        </>
      )}
    </PageScrollView>
  );
};
